/**
 * TanStack Query hooks for company lists. Every key starts with
 * `["company-lists"]`, so one invalidation (after an upload, a copy, a delete)
 * refreshes every list view.
 */
import type {
  CompanyListMembersPage,
  CompanyListOwnersResponse,
  CompanyListSummary,
  CompanyListsPage,
  CompanyPeople,
  CompanyRecord,
} from "@nu-assist/contracts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import type {
  CompanyListSortKey,
  CompanyListsClient,
  CompanyMemberSortKey,
  MemberFilterParams,
} from "../../clients/companyLists.client";
import type { OwnerOption } from "../../routes/research/imports/ImportOwnerMenu";
import { useStoredImageUrls } from "../media/useStoredImageUrls";
import { useVersionedKeys } from "../media/versionedKeys";

export const COMPANY_LISTS_KEY = ["company-lists"] as const;

/** Lists still being parsed poll until they settle, like people lists do. */
function isSettling(page: CompanyListsPage | undefined): boolean {
  return (page?.items ?? []).some((l) => l.status === "uploaded" || l.status === "parsing");
}

export function useCompanyLists(args: {
  client: CompanyListsClient;
  /** Null = every list in the firm. */
  searchId: string | null;
  page: number;
  pageSize: number;
  /** Name contains (debounced, trimmed); "" = no filter. */
  q?: string;
  /** Firm-wide only: narrow to one search. Ignored when `searchId` is set. */
  searchIdThrive?: string | null;
  /** "Imported by": `you` | `all` | an email. */
  owner?: string;
  /** Omitted = the API's default, newest first. */
  sort?: ListSort;
}) {
  const { client, searchId, page, pageSize, sort } = args;
  // Undefined, not "", so an unfiltered request carries no empty params.
  const q = args.q?.trim() || undefined;
  const owner = args.owner || undefined;
  const searchFilter = searchId === null ? args.searchIdThrive || undefined : undefined;
  const params = {
    page,
    page_size: pageSize,
    q,
    owner,
    sort_key: sort?.key,
    sort_dir: sort?.dir,
  };
  return useQuery<CompanyListsPage>({
    queryKey: [
      ...COMPANY_LISTS_KEY,
      "index",
      searchId ?? "all",
      searchFilter ?? null,
      q ?? "",
      owner ?? null,
      sort?.key ?? null,
      sort?.dir ?? null,
      page,
      pageSize,
    ],
    queryFn: () =>
      searchId === null
        ? client.listAll({ ...params, search_id_thrive: searchFilter })
        : client.listForSearch(searchId, params),
    placeholderData: (prev) => prev,
    refetchInterval: (q) => (isSettling(q.state.data) ? 3000 : false),
  });
}

export interface ListSort {
  key: CompanyListSortKey;
  dir: "asc" | "desc";
}

/** Same cache window as People Lists' owners (`useImportOwners`). */
const OWNERS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * The "Imported by" menu's options, from their own endpoint so they never
 * depend on the list under the menu: the Company Lists cut of People Lists'
 * `useImportOwners`, with the same result shape (`owners` is `null` while
 * loading, which the menu renders as "Loading recruiters…").
 */
export function useCompanyListOwners(client: CompanyListsClient): {
  query: UseQueryResult<OwnerOption[], Error>;
  owners: readonly OwnerOption[] | null;
} {
  const query = useQuery<CompanyListOwnersResponse, Error, OwnerOption[]>({
    queryKey: [...COMPANY_LISTS_KEY, "owners"],
    queryFn: () => client.listOwners(),
    select: (res) =>
      res.items.map((o) => ({ email: o.created_by_email, name: o.owner_name ?? null })),
    staleTime: OWNERS_STALE_TIME_MS,
  });
  return { query, owners: query.isPending ? null : (query.data ?? []) };
}

export function useCompanyList(client: CompanyListsClient, listId: string) {
  return useQuery<CompanyListSummary>({
    queryKey: [...COMPANY_LISTS_KEY, "one", listId],
    queryFn: () => client.get(listId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === "uploaded" || status === "parsing" ? 3000 : false;
    },
  });
}

export interface MemberSort {
  key: CompanyMemberSortKey;
  dir: "asc" | "desc";
}

export function useCompanyListMembers(args: {
  client: CompanyListsClient;
  listId: string;
  page: number;
  pageSize: number;
  sort: MemberSort;
  /** The detail page's filters; absent keys filter nothing. */
  filters?: MemberFilterParams;
}) {
  const { client, listId, page, pageSize, sort, filters = {} } = args;
  return useQuery<CompanyListMembersPage>({
    queryKey: [
      ...COMPANY_LISTS_KEY,
      "members",
      listId,
      page,
      pageSize,
      sort.key,
      sort.dir,
      JSON.stringify(filters),
    ],
    queryFn: () =>
      client.members(listId, {
        page,
        page_size: pageSize,
        sort_key: sort.key,
        sort_dir: sort.dir,
        ...filters,
      }),
    placeholderData: (prev) => prev,
  });
}

/** The company record behind a list row (panel + page). */
export function useCompanyRecord(client: CompanyListsClient, companyId: string | null) {
  return useQuery<CompanyRecord>({
    queryKey: [...COMPANY_LISTS_KEY, "record", companyId],
    queryFn: () => client.record(companyId as string),
    enabled: companyId !== null,
  });
}

export function useCompanyPeople(
  client: CompanyListsClient,
  companyId: string | null,
  enabled = true
) {
  return useQuery<CompanyPeople>({
    queryKey: [...COMPANY_LISTS_KEY, "people", companyId],
    queryFn: () => client.people(companyId as string),
    enabled: enabled && companyId !== null,
  });
}

/** What a person row carries for its photo: the stored key and its capture
 *  time in epoch seconds (the URL's `?v=`; see `versionedKeys`). */
export interface StoredAvatarRef {
  profile_image_s3_key?: string | null;
  profile_image_captured_at?: number | null;
}

/** People's stored avatars (`profile_image_s3_key`) → URLs (CloudFront or
 *  presigned, see `useStoredImageUrls`), for "People we know here". A photo
 *  with a capture time gets it as `?v=`, so a re-captured photo shows at once.
 *  A person without a stored avatar is not in the map; the caller shows
 *  initials. */
export function useAvatarUrls(
  client: CompanyListsClient,
  people: ReadonlyArray<StoredAvatarRef | null | undefined>
): ReadonlyMap<string, string> {
  const { s3Keys, versions } = useVersionedKeys(
    people.map((p) => [p?.profile_image_s3_key, p?.profile_image_captured_at] as const)
  );
  const presign = useCallback((k: string[]) => client.presignAvatars(k), [client]);
  return useStoredImageUrls("avatars", s3Keys, presign, "avatar-presign", versions).urls;
}

/** What a company row carries for its logo: the stored key and its capture
 *  time in epoch seconds (the URL's `?v=`; see `versionedKeys`). */
export interface StoredLogoRef {
  logo_s3_key?: string | null;
  logo_captured_at?: number | null;
}

/**
 * URLs for the given companies' logos, keyed by `logo_s3_key` (rows without a
 * key skipped), CloudFront or presigned. A logo with a capture time gets it as
 * `?v=`, so a re-captured logo shows at once instead of the cached old one.
 * Logos are cosmetic: an error or a missing key leaves the company on its
 * initials.
 */
export function useLogoUrls(
  client: CompanyListsClient,
  companies: ReadonlyArray<StoredLogoRef | null | undefined>
): ReadonlyMap<string, string> {
  const { s3Keys, versions } = useVersionedKeys(
    companies.map((c) => [c?.logo_s3_key, c?.logo_captured_at] as const)
  );
  const presign = useCallback((k: string[]) => client.presignLogos(k), [client]);
  return useStoredImageUrls("logos", s3Keys, presign, "logo-presign", versions).urls;
}
